import { AsyncPipe, NgClass, NgIf, NgTemplateOutlet } from '@angular/common';
import { AfterViewInit, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatBadge } from '@angular/material/badge';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatExpansionPanel, MatExpansionPanelHeader } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTab, MatTabContent, MatTabGroup, MatTabLabel } from '@angular/material/tabs';
import { Breadcrumb } from '@app/api/core/decorator/breadcrumb.decorator';
import { OAHelper } from '@app/api/core/utils/helper/offerteantraege-helper';
import { FilterSearchComponent } from '@app/shared/components/filter-search/filter-search.component';
import { MainContentComponent } from '@app/shared/components/main-content/main-content.component';
import { HasRightsDirective } from '@app/shared/directives/has-rights.directive';
import { UserTextService } from '@app/shared/services/data/user-text.service';
import { UserService } from '@app/shared/services/data/user.service';
import { ActiveTab } from '@app/shared/services/state/model/o30/activetab';
import { NichtFreigegebeneAntraegeComponent } from '@app/views/vertriebscockpit/offerteantraege/makler/nicht-freigegebene-antraege/nicht-freigegebene-antraege.component';
import { OffeneMaklerAntraegeComponent } from '@app/views/vertriebscockpit/offerteantraege/offene-makler-antraege/offene-makler-antraege.component';
import { ListData, ListDataConfig, ListTyp, PolizzeRowAddition } from '@app/views/vertriebscockpit/offerteantraege/offerteantraege.types';
import { InfotextComponent } from '@app/views/vertriebscockpit/start/infotext/infotext.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Payload, Person, PolizzeResult } from '@o30/entities';
import { DataService } from 'app/shared/services/data/data.service';
import { PageVertriebscockpitComponent } from 'app/views/vertriebscockpit/page/page.vertriebscockpit.component';
import { BehaviorSubject, firstValueFrom, Observable, Subject, Subscription } from 'rxjs';
import { filter, first } from 'rxjs/operators';
import { AntraegeComponent } from './antraege/antraege.component';
import { GenehmigungenComponent } from './genehmigungen/genehmigungen.component';
import { OffertstageoneComponent } from './offertstageone/offertstageone.component';
import { OffertstagetwoComponent } from './offertstagetwo/offertstagetwo.component';
import { OfferteAndAntraegeSchwebebestaendeComponent } from './schwebebestaende/schwebebestaende.component';
import { VerdorbeneComponent } from './verdorbene/verdorbene.component';
import { VorlaufigeDeckungenComponent } from './vorlaufige-deckungen/vorlaufige-deckungen.component';

@UntilDestroy()
@Breadcrumb({
  name: 'Offerte und Anträge'
})
@Component({
  selector:    'app-offerteantraege',
  templateUrl: './offerteantraege.component.html',
  styleUrls:   ['./offerteantraege.component.scss'],
  standalone:  true,
  // changeDetection: ChangeDetectionStrategy.OnPush, // TODO onPush or Signals for all tab components
  imports: [
    MainContentComponent,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatIcon,
    NgIf,
    MatCheckbox,
    FormsModule,
    FilterSearchComponent,
    MatTabGroup,
    NgClass,
    MatTab,
    MatTabLabel,
    MatProgressSpinner,
    MatTabContent,
    OffertstageoneComponent,
    OffertstagetwoComponent,
    AntraegeComponent,
    VerdorbeneComponent,
    MatBadge,
    GenehmigungenComponent,
    VorlaufigeDeckungenComponent,
    OfferteAndAntraegeSchwebebestaendeComponent,
    HasRightsDirective,
    AsyncPipe,
    NgTemplateOutlet,
    OffeneMaklerAntraegeComponent,
    NichtFreigegebeneAntraegeComponent,
    InfotextComponent
  ]
})
export class OfferteAndAntraegeComponent extends PageVertriebscockpitComponent implements OnInit,
                                                                                          OnDestroy,
                                                                                          AfterViewInit {
  private dataService = inject(DataService);
  protected userService = inject(UserService);
  protected userTextService = inject(UserTextService);

  @ViewChild('tabGroup', {static: true})
  tabGroup: MatTabGroup;

  protected readonly ActiveTab = ActiveTab;

  protected filterChangedSubject: Subject<void> = new Subject<void>();
  protected filterBySelectedPerson = true;

  protected selectedKunde: Person;
  protected iddBefreit = false;
  // Polint welche markiert werden soll (Sprung aus offert 3.0 nach speichern)
  protected markedPolintSubject: BehaviorSubject<number> = new BehaviorSubject(null);
  protected isFilterOpen = false;
  private actualSubscriptions: Map<ListTyp, Subscription> = new Map<ListTyp, Subscription>();

  // TODO refactor at some point, remove datasource members
  offertDataSource = new ListData();
  beratungEmpfehlungDataSource = new ListData();
  antragDataSource = new ListData();
  verdorbenerAntragDataSource = new ListData();
  genehmigungDataSource = new ListData();
  vorlDeckungDataSource = new ListData();
  schwebeDataSource = new ListData();
  lebenAenderungsOfferteDataSource = new ListData();
  // list of open antrag created by makler with checks to be completed by maklerbetreuer
  offeneMaklerAntraegeDataSource = new ListData();
  offeneUnbetreuteMaklerAntraegeDataSource = new ListData();
  // antrag for external makler that is not yet sent
  nichtFreigegebeneMaklerAntraegeDataSource = new ListData();

  // FIXME endpoints are called, even when the tab in question is not visible
  public readonly listDataConfig: Record<ListTyp, ListDataConfig> = {
    [ListTyp.OFFERT]:                             {
      getData:         this.dataService.getBenutzerOffert.bind(this.dataService),
      getDataForKunde: this.dataService.getBenutzerOffertKunde.bind(this.dataService),
      dataSource:      this.offertDataSource
    },
    [ListTyp.BERATUNG_EMPFEHLUNG]:                {
      getData:         this.dataService.getBenutzerOffert2.bind(this.dataService),
      getDataForKunde: this.dataService.getBenutzerOffert2Kunde.bind(this.dataService),
      getExpandedRows: this.getExpandedRowsWithOptionalDetail.bind(this),
      dataSource:      this.beratungEmpfehlungDataSource,
      right:           'O30_BERATUNG_EMPFEHLUNG'
    },
    [ListTyp.ANTRAG]:                             {
      getData:         this.dataService.getBenutzerAntrag.bind(this.dataService),
      getDataForKunde: this.dataService.getBenutzerAntragKunde.bind(this.dataService),
      dataSource:      this.antragDataSource,
      right:           'O30_ANTRAEGE'
    },
    [ListTyp.VERDORBENER_ANTRAG]:                 {
      getData:         this.dataService.getBenutzerVerdorben.bind(this.dataService),
      getDataForKunde: this.dataService.getBenutzerVerdorbenKunde.bind(this.dataService),
      dataSource:      this.verdorbenerAntragDataSource,
      right:           'O30_ANTRAG_VERDERBEN'
    },
    [ListTyp.GENHEMIGUNG]:                        {
      getData:         this.dataService.getBenutzerGenehmigung.bind(this.dataService),
      getDataForKunde: this.dataService.getBenutzerGenehmigungKunde.bind(this.dataService),
      getExpandedRows: this.getExpandedAndDetailRows.bind(this),
      dataSource:      this.genehmigungDataSource,
      right:           'O30_GENEHMIGUNG'
    },
    [ListTyp.VORL_DECKUNG]:                       {
      getData:         this.dataService.getBenutzerVorlDeckungPolizzen.bind(this.dataService),
      getDataForKunde: this.dataService.getBenutzerVorlDeckungKunde.bind(this.dataService),
      dataSource:      this.vorlDeckungDataSource,
      right:           'O30_VORLDECK'
    },
    [ListTyp.SCHWEBE]:                            {
      getData:         this.dataService.getBenutzerSchwebe.bind(this.dataService),
      getDataForKunde: this.dataService.getBenutzerSchwebeKunde.bind(this.dataService),
      dataSource:      this.schwebeDataSource
    },
    [ListTyp.LEBEN_AENDERUNGSOFFERT]:             {
      getData:         this.dataService.getBenutzerLebenaenderungsofferte.bind(this.dataService),
      getDataForKunde: this.dataService.getBenutzerLebenaenderungsofferteKunde.bind(this.dataService),
      dataSource:      this.lebenAenderungsOfferteDataSource,
      right:           'O30_AENDERUNGSANT_LEBEN'
    },
    [ListTyp.OFFENE_MAKLER_ANTRAEGE]:             {
      getData:         this.dataService.getBenutzerOffeneMaklerAntraege.bind(this.dataService),
      getDataForKunde: this.dataService.getBenutzerOffeneMaklerAntraegeKunde.bind(this.dataService),
      getExpandedRows: this.getExpandedAndDetailRows.bind(this),
      dataSource:      this.offeneMaklerAntraegeDataSource,
      right:           'O30_MAKPOL_ZUR_PRUEFUNG'
    },
    [ListTyp.OFFENE_UNBETREUTE_MAKLER_ANTRAEGE]:             {
      getData:         this.dataService.getBenutzerOffeneUnbetreuteMaklerAntraege.bind(this.dataService),
      getDataForKunde: this.dataService.getBenutzerOffeneMaklerAntraegeKunde.bind(this.dataService),
      getExpandedRows: this.getExpandedAndDetailRows.bind(this),
      dataSource:      this.offeneUnbetreuteMaklerAntraegeDataSource,
      right:           'O30_MAKPOL_ZUR_PRUEFUNG'
    },
    [ListTyp.NICHT_FREIGEGEBENE_MAKLER_ANTRAEGE]: {
      getData:         this.dataService.getBenutzerNichtFreigegebeneMaklerAntraege.bind(this.dataService),
      getDataForKunde: this.dataService.getBenutzerNichtFreigegebeneMaklerAntraegeKunde.bind(this.dataService),
      getExpandedRows: this.getExpandedAndDetailRows.bind(this),
      dataSource:      this.nichtFreigegebeneMaklerAntraegeDataSource,
      right:           'O30_MAKPOL_NICHT_FREIGEGEBEN'
    }
  };

  protected readonly allDataSources: Map<ListTyp, ListData> = new Map(
    Object.entries(this.listDataConfig)
      .map(([key, value]) => [
        key as ListTyp,
        value.dataSource
      ])
  );

  private readonly createDetailRow = (data: PolizzeResult) => ({
    detailRow:  true,
    isExpanded: false,
    data
  });

  private readonly createExpandedRow = (data: PolizzeResult) => ({
    expandedRow: true,
    isExpanded:  false,
    data
  });

  constructor() {
    super();
    this.showOfferteAntraegeLayoutComponents();
    this.userService.isAuthorized$('O30_IDDFREI')
      .pipe(
        first(),
        untilDestroyed(this))
      .subscribe(_hasRight => this.iddBefreit = _hasRight);
    this.initReloadSubjects();
  }

  private initReloadSubjects(): void {
    Object.keys(ListTyp)
      .map(key => ListTyp[key])
      .forEach(typ => {
        const ds = this.getDataSourceForTyp(typ);
        ds.reloadListSubject.asObservable()
          .pipe(untilDestroyed(this))
          .subscribe(value => {
            this.loadData(value || typ);
          });
      });
  }

  private initTabSelection() {
    this.llapi.getValue$('layout.activeTab')
      .pipe(
        filter(x => !!x),
        untilDestroyed(this)
      )
      .subscribe(_activeTab => {
        if (_activeTab) {
          // https://github.com/angular/components/issues/4136
          const index = this.tabGroup._tabs.toArray()
            .findIndex(
              (_item) => _item && _item.id === _activeTab);
          if (index !== -1) {
            this.tabGroup.selectedIndex = index;
          }
        }
      });
  }

  /** lifecycle hooks */
  ngOnInit(): void {
    this.initOfferteAntraegePage();
    this.initTabSelection();

    this.llapi.getValue$('actions.refresh.betreuer')
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(() => {
        this.selectedKunde = null;
        this.loadData();
      });

    this.llapi.patchValue('layout.spinner', false);
  }

  ngAfterViewInit(): void {
    this.llapi.getValue$('layout.activeOfferteAntraegeTab')
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(activeTab => this.optionalFocusTab(activeTab));
  }

  showOfferteAntraegeLayoutComponents(): void {
    this.llapi.patchValue('layout.pageLayout.breadcrumb', true);
  }

  ngOnDestroy(): void {
    this.llapi.patchValue('layout.spinner', true);
  }

  private initOfferteAntraegePage(): void {
    const {
      uiData,
      activePolint
    } = this.llapi.getValue('layout');

    if (activePolint) {
      this.markedPolintSubject.next(activePolint);
      this.llapi.resetValue('layout.activePolint');
    }

    if (uiData?.selectedPerson) {
      this.dataService.getPersonMinimalProperties(uiData.selectedPerson)
        .pipe(
          first(),
          untilDestroyed(this))
        .subscribe(_payload => {
          if (this.messageService.handlePayloadReturnSuccess(_payload)) {
            this.selectedKunde = _payload.data;
            this.loadData();
          }
        });
    } else {
      this.loadData();
    }
  }

  emitFilterChanged(filterInputValue: string): void {
    this.filterChangedSubject.next();
  }

  protected filterBySelectedPersonChanged() {
    this.llapi.patchValue('layout.shared.filterBySelectedPerson', this.filterBySelectedPerson);
    this.loadData();
  }

  loadData(filterTyp?: ListTyp): void {
    this.actualSubscriptions.forEach((subscription, key) => {
      if (!filterTyp || filterTyp === key) {
        subscription.unsubscribe();
        this.actualSubscriptions.delete(key);
      }
    });

    Object.values(ListTyp)
      .filter(typ => !filterTyp || typ === filterTyp)
      .forEach(typ => this.loadListDataForTyp(typ));
  }

  private async loadListDataForTyp(typ: ListTyp) {
    if (!this.listDataConfig[typ].right || await firstValueFrom(this.userService.isAuthorized$(this.listDataConfig[typ].right))) {
      const dataSource = this.getDataSourceForTyp(typ);
      dataSource.dataSource.data = [];
      dataSource.loadingFinished.next(false);
      dataSource.loading = true;

      const data$ = (this.filterBySelectedPerson && this.selectedKunde)
        ? this.getListDataForKunde(this.selectedKunde.id, typ)
        : this.getListData(typ);

      const subscription = data$
        .pipe(first(), untilDestroyed(this))
        .subscribe(_payload => {
          if (this.messageService.handlePayloadReturnSuccess(_payload)) {
            // https://stackoverflow.com/questions/48435515/how-to-flatten-nested-array-of-object-using-es6
            // don't use flatMap and for efficiency push into arr instead of concat
            dataSource.dataSource.data = _payload.data.reduce((arr, element) => {
              arr.push(OAHelper.createExpandableRow(element));
              for (const c of this.getExpandedRowAddition(element, typ)) {
                arr.push(c);
              }
              return arr;
            }, []);
            dataSource.loading = false;
            dataSource.loadingFinished.next(true);
            this.actualSubscriptions.delete(typ);
          }
        });

      this.actualSubscriptions.set(typ, subscription);
    }
  }

  private getDataSourceForTyp(typ: ListTyp): ListData {
    return this.allDataSources.get(typ);
  }

  getListData(listTyp: ListTyp): Observable<Payload<PolizzeResult[]>> {
    const endpoint = this.listDataConfig[listTyp];
    return endpoint.getData();
  }

  getListDataForKunde(kundeId: number, listTyp: ListTyp): Observable<Payload<PolizzeResult[]>> {
    const endpoint = this.listDataConfig[listTyp];
    return endpoint.getDataForKunde(kundeId);
  }

  public optionalFocusTab(activeTab: ActiveTab): void {
    try {
      if (activeTab) {
        let index = 0;
        for (const tab of this.tabGroup._tabs.toArray()) {
          if (tab.id === activeTab) {
            this.tabGroup.selectedIndex = index;
          }
          index++;
        }
        this.llapi.resetValue('layout.activeOfferteAntraegeTab');
      }
    } catch (error) {
      console.log('Could not select specified tab - check element path');
    }
  }

  public countGehmigungDocsMissing(): number {
    return this.genehmigungDataSource?.dataSource?.filteredData?.filter((data) => data?.data?.genehmDocsMissing)?.length / 3;
  }

  protected getExpandedRowAddition(data: PolizzeResult, listTyp: ListTyp): any[] {
    const config = this.listDataConfig[listTyp];
    return config.getExpandedRows
      ? config.getExpandedRows(data, this.iddBefreit)
      : [this.createExpandedRow(data)];
  }

  protected getExpandedRowsWithOptionalDetail(data: PolizzeResult) {
    const rows: PolizzeRowAddition[] = [this.createExpandedRow(data)];
    // Für IDD-Befreite den Teil mit Beratungsprotokoll drucken etc. nicht anzeigen
    if (!this.iddBefreit) {
      rows.push(this.createDetailRow(data));
    }
    return rows;
  }

  protected getExpandedAndDetailRows(data: PolizzeResult) {
    return [
      this.createExpandedRow(data),
      this.createDetailRow(data)
    ];
  }
}
